import { useEffect, useRef, useState } from 'react';

export function usePollingController(callback: () => void, intervalMs: number = 500) {
  const savedCallback = useRef(callback);
  const timerRef = useRef<number | null>(null);
  const shouldRunRef = useRef(false);

  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const clear = () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = () => {
    clear();
    if (document.hidden) {
      // dont start if hidden.
      setIsPaused(true);
      return;
    }
    setIsPaused(false);
    timerRef.current = window.setInterval(() => {
      savedCallback.current?.();
    }, intervalMs);
  };

  const start = () => {
    if (shouldRunRef.current) return;
    shouldRunRef.current = true;
    setIsRunning(true);
    startTimer();
  };

  const stop = () => {
    shouldRunRef.current = false;
    setIsRunning(false);
    setIsPaused(false);
    clear();
  };

  //pause when hidden, resume when visible.
  useEffect(() => {
    const onVis = () => {
      if (!shouldRunRef.current) return;
      if (document.hidden) {
        setIsPaused(true);
        clear();
      } else {
        startTimer();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [intervalMs]);

  useEffect(() => stop, []);

  return { start, stop, isRunning, isPaused } as const;
}

/**
 * Example
 *
 * function randomFunction() {
 *   const { start, stop, isRunning, isPaused } = usePollingController(() => aDifferentRandomFunction(), 500);
 *
 *   return (
 *     <div>
 *       <button onClick={start} disabled={isRunning && !isPaused}>Start</button>
 *       <button onClick={stop} disabled={!isRunning}>Stop</button>
 *       {isRunning && (isPaused ? <span>Paused (tab hidden)</span> : <span>Running</span>)}
 *     </div>
 *   );
 * }
 */
